import { useEffect, useState, useCallback } from 'react';
import { Check, EyeOff, Lock, RefreshCw } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { setUser } from 'actions/userActions';
import { trackEvent } from '../../helpers/analytics';
import { getItem } from '../../helpers/store';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';
import { EXTENSION_ID, checkExtensionAvailability } from '../../services/chromeExtension';
import { useShareContactsMutation } from '../../services/userV2';

export const EXTENSION_MESSAGE_TYPES = {
  IMPORT_CONTACTS_LINKEDIN_AUTH_ERROR: 'IMPORT_CONTACTS_LINKEDIN_AUTH_ERROR',
  SCRAPER_VOYAGER_IMPORT_SUCCESS: 'SCRAPER_VOYAGER_IMPORT_SUCCESS',
};

export const useLinkedInSync = ({
  onSyncStatusChange,
  isNetworkAdminOnboarding: isNetworkAdminOnboardingProp = false,
  isAllyOnboarding: isAllyOnboardingProp = false,
}) => {
  const { networkSlug } = useParams();

  // Detect onboarding context from URL if not explicitly passed as props
  const currentPath = window.location.pathname;
  const isNetworkAdminOnboarding =
    isNetworkAdminOnboardingProp ||
    (currentPath.includes('/onboarding/') &&
      currentPath.includes('/linkedin/') &&
      !currentPath.includes('/ally-signup/'));
  const isAllyOnboarding =
    isAllyOnboardingProp ||
    (currentPath.includes('/onboarding/') &&
      currentPath.includes('/ally-signup/') &&
      currentPath.includes('/linkedin/'));

  const user = useSelector(userSelector);
  let activeNetwork = useSelector(activeNetworkSelector);
  const isAlly = user?.isOnlyAlly;

  if (isAlly) {
    activeNetwork = user?.allyingCollections?.find((n) => n.slug === networkSlug);
  }

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVoyager, setIsLoadingVoyager] = useState(false);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const [responseType, setResponseType] = useState();
  const { action = 'sync' } = useParams();
  const dispatch = useDispatch();
  const [shareContacts] = useShareContactsMutation();
  const items = [
    {
      icon: RefreshCw,
      title: 'Automatic',
      description: 'Existing and new connections will be automatically added to Getro.',
    },
    {
      icon: EyeOff,
      title: 'Private',
      description: 'Your connections are only shared with networks you choose.',
    },
    {
      icon: Lock,
      title: 'Secure',
      description: 'Getro does not have access to or store your LinkedIn password.',
    },
  ];

  const syncItems = [
    {
      icon: Check,
      title: 'Extension installed',
      description: 'You’re all set to sync your contacts and explore all features.',
    },
    {
      icon: Check,
      title: 'Connections fetched',
      description: 'All your LinkedIn connections are now added to Getro.',
    },
    {
      icon: RefreshCw,
      title: 'Contacts are being enriched',
      description:
        'They will appear as they’re ready. This may take up to 3 hours; we’ll email you once it’s complete.',
    },
  ];

  const isLoadingOrLoadingVoyager = isLoading || isLoadingVoyager;
  const syncStatus = user?.linkedinSync?.syncStatus;
  const inSyncStatuses = ['active', 'syncing'];
  const isLinkedinSyncConnected = inSyncStatuses.includes(syncStatus);
  const syncCompleted = !isLoadingOrLoadingVoyager && isLinkedinSyncConnected;
  const synContacts = !isLoadingOrLoadingVoyager && !isLinkedinSyncConnected;
  const showSyncImage = !(isLinkedinSyncConnected && isAlly);

  const onConnect = useCallback(() => {
    // eslint-disable-next-line no-undef
    if (typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage) {
      setIsLoadingVoyager(true);
      // eslint-disable-next-line no-undef
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'SCRAPER_VOYAGER_INITIALIZE',
          auth: getItem('authToken'),
          user: {
            ...user,
            fullName: `${user.firstName} ${user.lastName}`,
          },
          networkId: activeNetwork?.id,
        },
        (response) => {
          setResponseType(response?.type);
          setIsLoadingVoyager(false);

          if (response?.type === EXTENSION_MESSAGE_TYPES.SCRAPER_VOYAGER_IMPORT_SUCCESS) {
            if (isNetworkAdminOnboarding) {
              trackEvent('admin_onboarding:linkedin_sync_complete', {
                collection_id: activeNetwork?.id,
              });
            } else if (isAllyOnboarding) {
              trackEvent('ally_onboarding:linkedin_sync_complete', {
                collection_id: activeNetwork?.id,
              });
            }

            dispatch(
              setUser({
                ...user,
                linkedinSync: {
                  syncStatus: 'syncing',
                  lastSyncedAt: new Date().toISOString(),
                  contactsSynced: 0,
                  contactsImportInProgress: true,
                  shareContacts: true,
                },
              }),
            );

            if (isNetworkAdminOnboarding && activeNetwork?.id && !activeNetwork?.shareContacts) {
              shareContacts({ collectionId: activeNetwork.id, share: true })
                .unwrap()
                .then(() => {
                  trackEvent('contacts:contacts_sharing:enabled', {
                    collectionId: activeNetwork.id,
                    page: 'onboarding',
                  });
                });
            }
          }

          onSyncStatusChange?.(response);
        },
      );
    }
  }, [
    user,
    activeNetwork?.id,
    activeNetwork?.shareContacts,
    onSyncStatusChange,
    dispatch,
    isNetworkAdminOnboarding,
    isAllyOnboarding,
    shareContacts,
  ]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await checkExtensionAvailability();
        clearInterval(interval);
        setIsExtensionInstalled(true);
        // Only auto-sync if user hasn't already synced
        if (!inSyncStatuses.includes(user?.linkedinSync?.syncStatus)) {
          onConnect();
        }
      } catch {
        // Do nothing
      } finally {
        if (isLoading) {
          setIsLoading(false);
        }
      }

      return () => clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const eventStart = `linkedin_sync:${action === 'sync' ? 'connect_flow' : 'reconnect'}:start`;
    const eventEnd = `linkedin_sync:${action === 'sync' ? 'connect_flow' : 'reconnect'}:success`;

    trackEvent(isLinkedinSyncConnected ? eventEnd : eventStart, {
      user: user.id,
    });
  }, [action, user, isLinkedinSyncConnected]);

  return {
    isLoading,
    isLoadingVoyager,
    isExtensionInstalled,
    activeNetwork,
    action,
    responseType,
    featureList: items,
    onConnect,
    syncItems,
    isAlly,
    isLoadingOrLoadingVoyager,
    syncCompleted,
    synContacts,
    showSyncImage,
    user,
  };
};
